(() => {
  const PRODUCT_SELECTOR = 'xo-product[xo-section-id]';
  const MOBILE_MEDIA_SELECTOR = '.main-product__media-mobile';
  const DESKTOP_MEDIA_SELECTOR = '[data-pdp-desktop-media]';
  const SLIDE_MEDIA_SELECTOR = '.xo-product-info-media-slide';
  const INITIALIZED_ATTRIBUTE = 'data-cortailor-variant-media-sync';
  const DEBOUNCE_MS = 120;
  const stateByProduct = new WeakMap();

  const getState = (product) => {
    let state = stateByProduct.get(product);
    if (!state) {
      state = {
        activeVariantId: '',
        pendingVariantId: '',
        requestId: 0,
        controller: null,
        timer: 0,
      };
      stateByProduct.set(product, state);
    }
    return state;
  };

  const getControlVariantId = (control) => {
    if (!control) return '';

    if (control instanceof HTMLSelectElement) {
      return control.selectedOptions[0]?.getAttribute('xo-variant-id') || '';
    }

    return control.getAttribute?.('xo-variant-id') || '';
  };

  const getSelectedVariantId = (product, fallbackId = '') => {
    return fallbackId
      || product.getAttribute('xo-selected-or-first-available-variant-id')
      || product.querySelector('xo-product-variants input:checked[xo-variant-id]')?.getAttribute('xo-variant-id')
      || product.querySelector('xo-product-variants select option:checked[xo-variant-id]')?.getAttribute('xo-variant-id')
      || '';
  };

  const getSectionId = (product) => product.getAttribute('xo-section-id') || '';

  const getReplacementSource = (html) => {
    const documentFragment = document.createElement('div');
    documentFragment.innerHTML = html;
    return documentFragment.querySelector(PRODUCT_SELECTOR) || documentFragment.querySelector('xo-product');
  };

  const replaceMedia = (product, sourceProduct) => {
    let replaced = false;
    const currentMobile = product.querySelector(MOBILE_MEDIA_SELECTOR);
    const nextMobile = sourceProduct.querySelector(MOBILE_MEDIA_SELECTOR);

    if (currentMobile && nextMobile) {
      currentMobile.replaceWith(nextMobile.cloneNode(true));
      replaced = true;
    }

    const currentDesktop = product.querySelector(DESKTOP_MEDIA_SELECTOR);
    const nextDesktop = sourceProduct.querySelector(DESKTOP_MEDIA_SELECTOR);

    if (currentDesktop && nextDesktop) {
      const replacement = nextDesktop.cloneNode(true);
      currentDesktop.replaceWith(replacement);
      window.CortailorPdpResponsiveMedia?.init(replacement);
      replaced = true;
    }

    if (!replaced) {
      const currentSlide = product.querySelector(SLIDE_MEDIA_SELECTOR);
      const nextSlide = sourceProduct.querySelector(SLIDE_MEDIA_SELECTOR);

      if (currentSlide && nextSlide) {
        currentSlide.replaceWith(nextSlide.cloneNode(true));
        replaced = true;
      }
    }

    if (replaced) {
      product.dispatchEvent(new CustomEvent('cortailor:variant-media-updated', { bubbles: true }));
    }

    return replaced;
  };

  const syncMedia = async (product, preferredVariantId = '') => {
    const sectionId = getSectionId(product);
    const variantId = getSelectedVariantId(product, preferredVariantId);

    if (!sectionId || !variantId) return;

    const state = getState(product);
    if (state.activeVariantId === variantId || state.pendingVariantId === variantId) return;

    state.pendingVariantId = variantId;
    state.requestId += 1;
    const requestId = state.requestId;

    state.controller?.abort();
    state.controller = new AbortController();

    const requestUrl = new URL(window.location.href);
    requestUrl.searchParams.set('variant', variantId);
    requestUrl.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(requestUrl.toString(), {
        credentials: 'same-origin',
        signal: state.controller.signal,
      });

      if (requestId !== state.requestId) return;

      if (!response.ok) {
        state.pendingVariantId = '';
        return;
      }

      const sourceProduct = getReplacementSource(await response.text());
      if (requestId !== state.requestId) return;

      if (sourceProduct && replaceMedia(product, sourceProduct)) {
        state.activeVariantId = variantId;
      }

      state.pendingVariantId = '';
    } catch (error) {
      if (requestId !== state.requestId) return;

      state.pendingVariantId = '';
      if (error?.name !== 'AbortError') {
        state.activeVariantId = '';
      }
    }
  };

  const scheduleSync = (product, preferredVariantId = '') => {
    const state = getState(product);
    window.clearTimeout(state.timer);

    state.timer = window.setTimeout(() => {
      syncMedia(product, preferredVariantId);
    }, DEBOUNCE_MS);
  };

  const initializeProduct = (product) => {
    if (product.hasAttribute(INITIALIZED_ATTRIBUTE)) return;

    product.setAttribute(INITIALIZED_ATTRIBUTE, 'true');
    getState(product).activeVariantId = getSelectedVariantId(product);

    product.addEventListener('change', (event) => {
      const control = event.target.closest?.('xo-product-variants input, xo-product-variants select');
      if (control) scheduleSync(product, getControlVariantId(control));
    }, true);

    product.addEventListener('input', (event) => {
      const control = event.target.closest?.('xo-product-variants input, xo-product-variants select');
      if (control) scheduleSync(product, getControlVariantId(control));
    }, true);

    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.attributeName === 'xo-selected-or-first-available-variant-id')) {
        scheduleSync(product);
      }
    });

    observer.observe(product, {
      attributes: true,
      attributeFilter: ['xo-selected-or-first-available-variant-id'],
    });
  };

  const initialize = (scope = document) => {
    if (scope.matches?.(PRODUCT_SELECTOR)) initializeProduct(scope);
    scope.querySelectorAll?.(PRODUCT_SELECTOR).forEach(initializeProduct);
  };

  if (window.CortailorVariantMediaSync) {
    window.CortailorVariantMediaSync.initialize(document);
    return;
  }

  window.CortailorVariantMediaSync = { initialize };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();